// Materials-specific duplicate finder: per-group Keep selection + Merge
// into keep (full merge API). Wraps the generic DuplicateFinderPanel.
import { useCallback, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import {
  mergeManufacturingMaterials,
  type MfgMaterialMergeResponse,
} from "@/lib/manufacturing/materialsMergeClient";
import { ManufacturingDuplicateFinderPanel } from "./ManufacturingDuplicateFinderPanel";
import { ManufacturingMaterialDuplicateRow } from "./ManufacturingMaterialDuplicateRow";
import { ManufacturingMaterialMergePreviewModal } from "./ManufacturingMaterialMergePreviewModal";

type DeactivateResult = { ok: true } | { ok: false; error: string };

type Props = {
  materials: ReadonlyArray<MfgMaterial>;
  busy: boolean;
  canMerge: boolean;
  onClose: () => void;
  onEdit: (material: MfgMaterial) => void;
  onDeactivate: (material: MfgMaterial) => Promise<DeactivateResult>;
  onMerged: (summary: { fromName: string; toName: string }) => void;
};

export function ManufacturingMaterialDuplicatesPanel({
  materials,
  busy,
  canMerge,
  onClose,
  onEdit,
  onDeactivate,
  onMerged,
}: Props) {
  // groupKey → keeper material id
  const [keepersByGroup, setKeepersByGroup] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<MfgMaterialMergeResponse | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBlockers, setPreviewBlockers] = useState<
    Array<{ code: string; message: string }>
  >([]);
  const [pendingFromId, setPendingFromId] = useState<string | null>(null);
  const [pendingToId, setPendingToId] = useState<string | null>(null);

  const setKeeper = useCallback((groupKey: string, materialId: string) => {
    setKeepersByGroup((prev) => ({ ...prev, [groupKey]: materialId }));
  }, []);

  async function openMergePreview(from: MfgMaterial, to: MfgMaterial) {
    setPreviewBusy(true);
    setPreviewError(null);
    setPreviewBlockers([]);
    setPendingFromId(from.id);
    setPendingToId(to.id);
    const res = await mergeManufacturingMaterials({
      from_material_id: from.id,
      to_material_id: to.id,
      dry_run: true,
    });
    setPreviewBusy(false);
    if (!res.ok) {
      setPreviewError(res.error);
      setPreview({
        from_code: from.code,
        from_name: from.name,
        to_code: to.code,
        to_name: to.name,
      });
      return;
    }
    setPreview(res.data);
  }

  async function confirmMerge() {
    if (!pendingFromId || !pendingToId) return;
    setPreviewBusy(true);
    setPreviewError(null);
    setPreviewBlockers([]);
    const res = await mergeManufacturingMaterials({
      from_material_id: pendingFromId,
      to_material_id: pendingToId,
      dry_run: false,
    });
    setPreviewBusy(false);
    if (!res.ok) {
      const blockers =
        res.body &&
        typeof res.body === "object" &&
        res.body !== null &&
        "blockers" in res.body &&
        Array.isArray((res.body as { blockers: unknown }).blockers)
          ? ((res.body as { blockers: Array<{ code: string; message: string }> }).blockers ?? [])
          : [];
      setPreviewError(res.error);
      setPreviewBlockers(blockers);
      return;
    }
    const fromName = res.data.from_name ?? "discard";
    const toName = res.data.to_name ?? "keeper";
    setPreview(null);
    setPendingFromId(null);
    setPendingToId(null);
    onMerged({ fromName, toName });
  }

  return (
    <>
      <ManufacturingDuplicateFinderPanel<MfgMaterial>
        items={materials}
        getName={(m) => m.name}
        getKey={(m) => m.id}
        onClose={onClose}
        entityPlural="materials"
        renderRow={(m, _indexInGroup, groupKey) => {
          const keeperId = keepersByGroup[groupKey] ?? "";
          const isKeeper = keeperId === m.id;
          return (
            <ManufacturingMaterialDuplicateRow
              material={m}
              busy={busy || previewBusy}
              isKeeper={isKeeper}
              canMerge={canMerge}
              mergeEnabled={canMerge && !!keeperId && !isKeeper}
              onEdit={onEdit}
              onDeactivate={onDeactivate}
              onMarkKeep={() => setKeeper(groupKey, m.id)}
              onRequestMerge={() => {
                const keeper = materials.find((x) => x.id === keeperId);
                if (!keeper) return;
                void openMergePreview(m, keeper);
              }}
            />
          );
        }}
      />
      {preview ? (
        <ManufacturingMaterialMergePreviewModal
          preview={preview}
          busy={previewBusy}
          error={previewError}
          blockers={previewBlockers}
          onCancel={() => {
            if (previewBusy) return;
            setPreview(null);
            setPendingFromId(null);
            setPendingToId(null);
            setPreviewError(null);
            setPreviewBlockers([]);
          }}
          onConfirm={() => void confirmMerge()}
        />
      ) : null}
    </>
  );
}
